import {certificationBadges, footerDataContent} from '@/util/contents/footer-data';
import {socialMediaData} from '@/util/contents/social-media-data';
import Link from 'next/link';
import React, {memo, useState} from 'react';
import Container from '@/components/atoms/Container';
import Icon from '@/components/atoms/Icon';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './LatestFooter.module.scss';

const LatestFooter = () => {
    const [isShow, setIsShow] = useState(-1);
    const [openIndex, setOpenIndex] = useState<number | null>(null);

    const handleToggle = (index: number) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    return (
        <div className={`mobile-bottom-padding mobile-top-padding ${styles.wrapper}`}>
            <Container>
                <div>
                    <div className={styles.grid}>
                        {footerDataContent?.map((item: any, index: number) => (
                            <div className={styles.column} key={index + 1}>
                                <div>
                                    <button
                                        type="button"
                                        onClick={() => handleToggle(index)}
                                        aria-expanded={openIndex === index}
                                        aria-controls={`footer-column-${index}`}
                                        className={styles.columnHeading}
                                    >
                                        <span className={`font-headers ${styles.columnTitle}`}>{item.heading}</span>

                                        <span className={styles.toggleIcon}>
                                            <Icon name={openIndex === index ? 'Remove' : 'Add'} />
                                        </span>
                                    </button>

                                    {/* Links always exist in the HTML. Collapsed only hides them below 426px —
                                        above it the full list is always visible. */}
                                    <div
                                        id={`footer-column-${index}`}
                                        className={openIndex === index ? styles.linkGroupOpen : styles.linkGroupClosed}
                                    >
                                        {item.values?.map((subItem: any, subIndex: number) =>
                                            subItem?.subChild ? (
                                                <div key={subIndex + 1}>
                                                    <p className={`font-headers ${styles.subChildLabel}`}>{subItem.label}</p>

                                                    {subItem?.subChildItem?.map((secondPart: any) => (
                                                        <div className={styles.linkBox} key={secondPart.path}>
                                                            <Link href={secondPart.path} className={`font-regular-text ${styles.link}`}>
                                                                {secondPart.label}
                                                            </Link>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div key={subIndex + 1} className={styles.linkBox}>
                                                    <Link href={subItem.path} className={`font-regular-text ${styles.link}`}>
                                                        {subItem.label}
                                                    </Link>
                                                </div>
                                            )
                                        )}

                                        {item?.isMore && (
                                            <>
                                                {/* `more` links: always shown on mobile (the accordion reveals everything),
                                                    gated behind the More…/Less… toggle on desktop only. */}
                                                <div className={isShow === index ? undefined : styles.moreCollapsed}>
                                                    {item?.more?.map((child: any, childIndex: number) => (
                                                        <div key={childIndex + 1} className={styles.linkBox}>
                                                            <Link href={child.path} className={`font-regular-text ${styles.link}`}>
                                                                {child.label}
                                                            </Link>
                                                        </div>
                                                    ))}
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => setIsShow(isShow === index ? -1 : index)}
                                                    aria-expanded={isShow === index}
                                                    className={`font-regular-text ${styles.moreToggle}`}
                                                >
                                                    {isShow === index ? 'Less...' : 'More...'}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Container>

            <div className={styles.certStrip}>
                <Container>
                    <div className={styles.certRow}>
                        {certificationBadges.map((badge) => (
                            <div key={badge.title} className={styles.certBadge}>
                                <div className={styles.certIcon}>
                                    {badge.src && <OptimizedImage src={badge.src} alt={badge.title} width={64} height={64} />}
                                </div>
                                <div>
                                    <p className={styles.certTitle}>{badge.title}</p>
                                    <p className={styles.certSubtitle}>{badge.subtitle}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </Container>
            </div>

            <Container>
                <div className={styles.socialRow}>
                    {socialMediaData?.map((item: any, index: number) => (
                        <div key={index + 1} className={styles.socialIcon}>
                            <Link href={item.redirect} target="_blank" rel="noopener noreferrer">
                                <OptimizedImage src={item.icon} alt="social media" width={36} height={36} className={styles.socialImg} />
                            </Link>
                        </div>
                    ))}
                </div>
            </Container>
        </div>
    );
};

export default memo(LatestFooter);
